// TestResultsContext.tsx - Comprehensive Test Results Management
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';

// TYPE DEFINITIONS

/**
 * Complete test result record
 * Contains patient info, test details, results, and communication details
 *
 * @property id - Unique test result identifier (TEST-YYYY-XXX format)
 * @property patientName - Full name of patient tested
 * @property patientId - Unique patient identifier
 * @property age - Patient age at time of test
 * @property gender - Patient gender
 * @property requestedBy - Doctor/clinician name who requested the test
 * @property hospital - Healthcare facility name
 * @property testType - Type of laboratory test performed
 * @property completedDate - Date test was completed (YYYY-MM-DD format)
 * @property completedTime - Time test was completed (HH:MM format)
 * @property turnaroundTime - Time from sample to result (e.g., "4h 15m")
 * @property technician - Lab technician who processed the test
 * @property resultStatus - Current status of result communication
 * @property resultFile - Filename of the result report/PDF
 * @property findings - Text summary of test findings
 * @property patientPhone - Patient contact phone number
 * @property doctorPhone - Doctor contact phone number
 * @property patientEmail - Patient email address
 * @property doctorEmail - Doctor email address
 *
 * @example
 * const result: TestResult = {
 *   id: "TEST-2025-075",
 *   patientName: "Ngozi Okonkwo",
 *   patientId: "PT-98765",
 *   age: 38,
 *   gender: "Female",
 *   requestedBy: "Dr. Sarah Wilson",
 *   hospital: "City General Hospital",
 *   testType: "Complete Blood Count (CBC)",
 *   completedDate: "2025-10-23",
 *   completedTime: "14:30",
 *   turnaroundTime: "4h 15m",
 *   technician: "Jane Smith",
 *   resultStatus: "sent",
 *   resultFile: "CBC_Report_PT98765.pdf",
 *   findings: "All parameters within normal range.",
 *   patientPhone: "+234 801 234 5678",
 *   doctorPhone: "+234 802 345 6789",
 *   patientEmail: "ngozi.okonkwo@email.com",
 *   doctorEmail: "dr.wilson@cityhospital.com",
 * };
 */
export interface TestResult {
  id: string;
  patientName: string;
  patientId: string;
  age: number;
  gender: string;
  requestedBy: string;
  hospital: string;
  testType: string;
  completedDate: string;
  completedTime: string;
  turnaroundTime: string;
  technician: string;
  resultStatus: 'sent' | 'pending' | 'viewed';
  resultFile: string;
  findings: string;
  patientPhone?: string;
  doctorPhone?: string;
  patientEmail?: string;
  doctorEmail?: string;
}

/**
 * Complete test results context API
 * Provides all methods for managing, searching, and analyzing test results
 */
interface TestResultsContextType {
  // State
  testResults: TestResult[];

  // CRUD Operations
  addTestResult: (result: TestResult) => void;
  updateTestStatus: (id: string, status: 'sent' | 'pending' | 'viewed') => void;
  getTestById: (id: string) => TestResult | undefined;
  deleteTestResult: (id: string) => void;

  // Search & Filter
  searchResults: (query: string) => TestResult[];
  filterByStatus: (status: 'sent' | 'pending' | 'viewed') => TestResult[];
  filterByDateRange: (startDate: string, endDate: string) => TestResult[];

  // Bulk Operations
  bulkDelete: (ids: string[]) => void;
  bulkUpdateStatus: (ids: string[], status: 'sent' | 'pending' | 'viewed') => void;

  // LocalStorage Management
  clearAllData: () => void;
  resetToDefaults: () => void;
  exportResults: () => void;

  // Analytics
  getTotalResults: () => number;
  getResultsByStatus: () => { sent: number; pending: number; viewed: number };
}

// TODO: BACKEND INTEGRATION - REMOVE BELOW WHEN CONNECTED TO API

/**
 * TODO: BACKEND - Replace with API call to GET /api/lab/test-results
 * Sample test results data - remove when backend is integrated
 */
const DEFAULT_TEST_RESULTS: TestResult[] = [];

/**
 * TODO: BACKEND - Remove localStorage keys - use API endpoints instead
 * Local storage keys for persistence
 */
const STORAGE_KEYS = {
  TEST_RESULTS: 'glucosecare_test_results',
};

/**
 * TODO: SECURITY & LOGGING - Replace console.error with centralized error tracking
 * Implement proper error logging service (Sentry, Datadog, etc.) instead of console
 * TODO: BACKEND - Remove this helper - use API calls instead
 * Loads data from localStorage with fallback to default values
 */
const loadFromLocalStorage = <T,>(key: string, defaultValue: T): T => { return defaultValue; };

/**
 * TODO: SECURITY & LOGGING - Replace console.error with centralized error tracking
 * Implement proper error logging service (Sentry, Datadog, etc.) instead of console
 * TODO: BACKEND - Remove this helper - use API calls instead
 * Saves data to localStorage
 */
const saveToLocalStorage = <T,>(key: string, value: T): void => { /* Test results require backend persistence. */ };

// END OF SECTION TO REMOVE

// CONTEXT CREATION

const TestResultsContext = createContext<TestResultsContextType | undefined>(undefined);

// PROVIDER COMPONENT

/**
 * TestResultsProvider - Manages all test results state and operations
 * Provides comprehensive test results management API throughout the application
 * TODO: SECURITY - All console logging removed to prevent exposure of internal state
 */
export function TestResultsProvider({ children }: { children: ReactNode }) {
  // TODO: BACKEND - Replace with API call to GET /api/lab/test-results
  // Initialize state from localStorage - REMOVE THIS
  const [testResults, setTestResults] = useState<TestResult[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.TEST_RESULTS, DEFAULT_TEST_RESULTS)
  );

   // AUTO-SAVE EFFECTS - TODO: REMOVE THESE WHEN CONNECTED TO BACKEND

  // TODO: BACKEND - Replace with API call to PUT /api/lab/test-results
  // Auto-save test results to localStorage - REMOVE THIS
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.TEST_RESULTS, testResults);
  }, [testResults]);

  // CRUD OPERATIONS

  /**
   * Adds a new test result to the system
   * New results are added to the beginning of the array
   *
   * TODO: BACKEND - POST /api/lab/test-results
   */
  const addTestResult = (result: TestResult) => {
    setTestResults((prev) => [result, ...prev]);
    toast.success('Test result added successfully');
  };

  /**
   * Updates the status of a specific test result
   * Status types: 'sent', 'pending', 'viewed'
   *
   * TODO: BACKEND - PUT /api/lab/test-results/{id}/status
   */
  const updateTestStatus = (id: string, status: 'sent' | 'pending' | 'viewed') => {
    setTestResults((prev) =>
      prev.map((test) =>
        test.id === id ? { ...test, resultStatus: status } : test
      )
    );
    toast.success('Test result status updated');
  };

  /**
   * Retrieves a specific test result by ID
   * Returns undefined if not found
   */
  const getTestById = (id: string): TestResult | undefined => {
    return testResults.find((test) => test.id === id);
  };

  /**
   * Deletes a specific test result from the system
   *
   * TODO: BACKEND - DELETE /api/lab/test-results/{id}
   */
  const deleteTestResult = (id: string) => {
    setTestResults((prev) => prev.filter((test) => test.id !== id));
    toast.success('Test result deleted');
  };

  // SEARCH & FILTER OPERATIONS

  /**
   * Searches test results by multiple fields
   * Searches across: patient name, patient ID, test ID, test type, hospital
   * Case-insensitive search
   *
   * TODO: BACKEND - GET /api/lab/test-results/search with query parameter
   */
  const searchResults = (query: string): TestResult[] => {
    const lowerQuery = query.toLowerCase();
    return testResults.filter(
      (result) =>
        result.patientName.toLowerCase().includes(lowerQuery) ||
        result.patientId.toLowerCase().includes(lowerQuery) ||
        result.id.toLowerCase().includes(lowerQuery) ||
        result.testType.toLowerCase().includes(lowerQuery) ||
        result.hospital.toLowerCase().includes(lowerQuery)
    );
  };

  /**
   * Filters test results by communication status
   *
   * @param status - Result status: 'sent', 'pending', or 'viewed'
   * @returns Array of test results matching the status
   *
   * TODO: BACKEND - GET /api/lab/test-results/filter/status
   */
  const filterByStatus = (status: 'sent' | 'pending' | 'viewed'): TestResult[] => {
    return testResults.filter((result) => result.resultStatus === status);
  };

  /**
   * Filters test results by completion date range
   *
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Array of test results within date range
   *
   * TODO: BACKEND - GET /api/lab/test-results/filter/date-range
   */
  const filterByDateRange = (startDate: string, endDate: string): TestResult[] => {
    return testResults.filter((result) => {
      const resultDate = new Date(result.completedDate);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return resultDate >= start && resultDate <= end;
    });
  };

  // BULK OPERATIONS

  /**
   * Deletes multiple test results at once
   *
   * @param ids - Array of test result IDs to delete
   *
   * TODO: BACKEND - DELETE /api/lab/test-results/bulk
   */
  const bulkDelete = (ids: string[]) => {
    setTestResults((prev) => prev.filter((result) => !ids.includes(result.id)));
    toast.success(`${ids.length} test results deleted`);
  };

  /**
   * Updates status for multiple test results at once
   *
   * @param ids - Array of test result IDs to update
   * @param status - New status for all specified results
   *
   * TODO: BACKEND - PUT /api/lab/test-results/bulk/status
   */
  const bulkUpdateStatus = (ids: string[], status: 'sent' | 'pending' | 'viewed') => {
    setTestResults((prev) =>
      prev.map((result) =>
        ids.includes(result.id) ? { ...result, resultStatus: status } : result
      )
    );
    toast.success(`${ids.length} test results updated`);
  };

  // LOCALSTORAGE MANAGEMENT

  /**
   * Clears all test results data from storage
   * WARNING: This is irreversible
   *
   * TODO: BACKEND - Consider if this operation is needed with API backend
   */
  const clearAllData = () => {
    // TODO: BACKEND - Remove localStorage operations
    localStorage.removeItem(STORAGE_KEYS.TEST_RESULTS);
    setTestResults([]);
    toast.success('All test results cleared');
  };

  /**
   * Resets all test results to default values
   * WARNING: This is irreversible
   *
   * TODO: BACKEND - Remove this function when using API backend
   */
  const resetToDefaults = () => {
    setTestResults(DEFAULT_TEST_RESULTS);
    toast.success('Test results reset to defaults');
  };

  /**
   * Exports all test results as a JSON file
   * Downloads to user's device with date stamp
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const exportResults = () => {
    try {
      const dataStr = JSON.stringify(testResults, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `test-results-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Test results exported successfully');
    } catch (error) {
      // TODO: SECURITY & LOGGING - Use centralized error tracking (Sentry, etc.) instead of console.error
      toast.error('Failed to export test results');
    }
  };

  // ANALYTICS & REPORTING

  /**
   * Gets the total count of all test results
   *
   * @returns Total number of test results
   */
  const getTotalResults = (): number => {
    return testResults.length;
  };

  /**
   * Gets count breakdown of test results by status
   *
   * @returns Object with counts for 'sent', 'pending', and 'viewed' statuses
   */
  const getResultsByStatus = () => {
    return {
      sent: testResults.filter((r) => r.resultStatus === 'sent').length,
      pending: testResults.filter((r) => r.resultStatus === 'pending').length,
      viewed: testResults.filter((r) => r.resultStatus === 'viewed').length,
    };
  };

  // PROVIDER VALUE EXPORT

  return (
    <TestResultsContext.Provider
      value={{
        testResults,
        addTestResult,
        updateTestStatus,
        getTestById,
        deleteTestResult,
        searchResults,
        filterByStatus,
        filterByDateRange,
        bulkDelete,
        bulkUpdateStatus,
        clearAllData,
        resetToDefaults,
        exportResults,
        getTotalResults,
        getResultsByStatus,
      }}
    >
      {children}
    </TestResultsContext.Provider>
  );
}

// CUSTOM HOOK

/**
 * useTestResults hook - Access test results context anywhere in the application
 * Must be used within a TestResultsProvider
 *
 * @throws Error if used outside of TestResultsProvider
 * @returns {TestResultsContextType} Complete test results management API
 *
 * @example
 * const { testResults, addTestResult, searchResults } = useTestResults();
 */
export function useTestResults() {
  const context = useContext(TestResultsContext);
  if (context === undefined) {
    throw new Error('useTestResults must be used within a TestResultsProvider');
  }
  return context;
}